import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { RefreshCw, Save, Plus, Trash2, FlaskConical, AlertCircle, ChevronDown, ChevronRight } from "lucide-react";

/**
 * Per-program Extension Config editor (step-machine schema).
 *
 * Schema (server-side):
 *   {
 *     sync_method:    "steps",
 *     login_url:      string?,
 *     logged_in_check_url: string?,
 *     logged_in_when: string?,
 *     steps: [{
 *       id, order, label,
 *       detect: { url_re, predicate? },
 *       wait_until?, settle_ms, retry: { max_attempts, interval_ms },
 *       extractors: [
 *         { kind:"net_intercept", id, url_re, method, mappings, sample_response? }
 *         | { kind:"inline_js",   id, fields }
 *         | { kind:"dom_html",    id, fields, sample_html? }
 *         | { kind:"fetch_json",  id, url_template, mappings, sample_response? }
 *         | { kind:"fetch_html",  id, url_template, anchor_regex?, fields, sample_html? }
 *       ],
 *       advance: { next_step_id?, next_url?, terminate, on_timeout }
 *     }],
 *     cert_codes:     { code -> label }
 *   }
 */

const KNOWN_FIELDS = ["balance", "tier", "holder", "member_id", "lifetime_miles", "pooled_balance", "pqp", "pqf"];
const FIELD_KINDS  = ["inline_path", "dom_text", "dom_list"];
const EXTRACTOR_KINDS = ["net_intercept", "inline_js", "dom_html", "fetch_json", "fetch_html"];

// ---------------------------------------------------------------------------
// PredicateField — text input for a JS predicate (used by logged_in_when,
// step.wait_until, step.detect.predicate). Includes a "Build from element"
// helper so admins can express common checks (element exists, text equals,
// text is not in a list of values like ["Sign In","Login"]) without writing
// JS by hand. The generated expression is plain JS the injector can eval,
// so the stored shape stays compatible with existing configs.
// ---------------------------------------------------------------------------
function buildPredicateFromSelector({ op, selector, value }) {
  const sel = (selector || "").trim();
  if (!sel) return "";
  const esc = (s) => String(s).replace(/\\/g, "\\\\").replace(/'/g, "\\'");
  if (op === "exists") {
    return `!!document.querySelector('${esc(sel)}')`;
  }
  if (op === "text_equals") {
    return `document.querySelector('${esc(sel)}')?.textContent?.trim() === '${esc(value || "")}'`;
  }
  if (op === "text_not_in") {
    const values = String(value || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const arr = `[${values.map((v) => `'${esc(v)}'`).join(",")}]`;
    return `!${arr}.includes(document.querySelector('${esc(sel)}')?.textContent?.trim())`;
  }
  if (op === "text_includes") {
    return `(document.querySelector('${esc(sel)}')?.textContent || '').includes('${esc(value || "")}')`;
  }
  return "";
}

function PredicateField({ value, onChange, placeholder, dataTestid }) {
  const [open, setOpen] = useState(false);
  const [op, setOp] = useState("exists");
  const [selector, setSelector] = useState("");
  const [opValue, setOpValue] = useState("");

  function apply() {
    const expr = buildPredicateFromSelector({ op, selector, value: opValue });
    if (expr) onChange(expr);
    setOpen(false);
  }

  return (
    <div className="space-y-1">
      <input
        className="input font-mono text-[12px]"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={dataTestid}
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-[10.5px] text-neutral-600 hover:text-black underline decoration-dotted"
        >
          {open ? "Hide" : "Build from element"}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-[10.5px] text-neutral-500 hover:text-red-700"
          >
            Clear
          </button>
        )}
      </div>
      {open && (
        <div className="border border-black/10 rounded-[6px] p-2 bg-black/[0.02] space-y-2">
          <div className="grid grid-cols-12 gap-2">
            <label className="block col-span-7">
              <div className="text-[10px] uppercase tracking-[0.12em] text-neutral-500 mb-1">CSS selector</div>
              <input className="input text-[12px] font-mono" value={selector} onChange={(e) => setSelector(e.target.value)} placeholder=".header-account-link" />
            </label>
            <label className="block col-span-5">
              <div className="text-[10px] uppercase tracking-[0.12em] text-neutral-500 mb-1">check</div>
              <select className="input text-[12px]" value={op} onChange={(e) => setOp(e.target.value)}>
                <option value="exists">element exists</option>
                <option value="text_equals">text equals</option>
                <option value="text_not_in">text is NOT one of</option>
                <option value="text_includes">text includes</option>
              </select>
            </label>
            {op !== "exists" && (
              <label className="block col-span-12">
                <div className="text-[10px] uppercase tracking-[0.12em] text-neutral-500 mb-1">
                  {op === "text_not_in" ? "values (comma-separated)" : "value"}
                </div>
                <input className="input text-[12px] font-mono" value={opValue} onChange={(e) => setOpValue(e.target.value)} placeholder={op === "text_not_in" ? "Sign In, Login, Log In" : "Welcome"} />
              </label>
            )}
          </div>
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="btn btn-ghost text-[11.5px]">Cancel</button>
            <button type="button" onClick={apply} disabled={!selector.trim()} className="btn btn-primary text-[11.5px]">Use this expression</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// JSON tree (sample inspector for mappings)
// ---------------------------------------------------------------------------
function flattenJson(value, basePath = "", maxDepth = 6) {
  const rows = [];
  function walk(node, path, depth, key, parentIsArray) {
    if (depth > maxDepth) return;
    const isObject = node !== null && typeof node === "object";
    const isArray  = Array.isArray(node);
    const childCount = isObject ? (isArray ? node.length : Object.keys(node).length) : 0;
    rows.push({
      path, depth, key: key === undefined ? null : key,
      parentIsArray, isObject, isArray, childCount,
      value: isObject ? null : node,
    });
    if (!isObject) return;
    if (isArray) node.forEach((v, i) => walk(v, `${path}[${i}]`, depth + 1, i, true));
    else for (const [k, v] of Object.entries(node)) walk(v, path ? `${path}.${k}` : String(k), depth + 1, k, false);
  }
  walk(value, basePath, 0, undefined, false);
  return rows;
}

function JsonTree({ value }) {
  const [expanded, setExpanded] = useState(() => new Set([""]));
  const rows = useMemo(() => flattenJson(value), [value]);
  const visible = useMemo(() => {
    const out = []; let hiddenBelowDepth = null;
    for (const r of rows) {
      if (hiddenBelowDepth !== null && r.depth > hiddenBelowDepth) continue;
      hiddenBelowDepth = null;
      out.push(r);
      if (r.isObject && !expanded.has(r.path)) hiddenBelowDepth = r.depth;
    }
    return out;
  }, [rows, expanded]);
  function toggle(path) {
    setExpanded((prev) => { const n = new Set(prev); if (n.has(path)) n.delete(path); else n.add(path); return n; });
  }
  function copy(text) { try { navigator.clipboard.writeText(text); } catch {} }
  return (
    <div className="text-[11.5px] font-mono leading-snug">
      {visible.map((r) => {
        const indent = { paddingLeft: r.depth * 12 + "px" };
        const labelText = r.key === null
          ? (r.isArray ? `[${r.childCount}]` : r.isObject ? `{${r.childCount}}` : "")
          : (r.parentIsArray ? `[${r.key}]` : String(r.key));
        return (
          <div key={r.path || "(root)"} style={indent} className="flex items-center gap-1">
            {r.isObject ? (
              <button type="button" onClick={() => toggle(r.path)} className="text-neutral-500 hover:text-black">
                {expanded.has(r.path) ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
              </button>
            ) : <span style={{ width: 11, display: "inline-block" }} />}
            <button type="button" onClick={() => copy(r.path)} className="text-neutral-600 hover:text-black hover:bg-yellow-50 rounded px-1" title={`Copy path: ${r.path || "(root)"}`}>
              {labelText}
            </button>
            {r.isObject ? (
              <span className="text-neutral-400">{r.isArray ? ` [${r.childCount}]` : ` {${r.childCount}}`}</span>
            ) : (
              <span className="text-neutral-700">: {typeof r.value === "string" ? `"${r.value}"` : String(r.value)}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FieldSpec editor — used by inline_js / dom_html / fetch_html extractors.
// ---------------------------------------------------------------------------
function ItemFieldRow({ fieldName, spec, onRename, onChange, onDelete }) {
  return (
    <div className="border border-black/10 rounded-[6px] p-2 bg-white" data-testid={`item-field-${fieldName}`}>
      <div className="flex items-center gap-2">
        <input
          className="input flex-1 text-[12px]"
          value={fieldName}
          onChange={(e) => onRename(e.target.value)}
          placeholder="label"
        />
        <span className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 font-mono">dom_text</span>
        <button onClick={onDelete} className="text-neutral-500 hover:text-red-700" title="Delete sub-field">
          <Trash2 size={12} />
        </button>
      </div>
      <input
        className="input font-mono text-[11.5px] mt-1.5"
        value={spec?.selector || ""}
        onChange={(e) => onChange({ kind: "dom_text", selector: e.target.value })}
        placeholder=".name"
      />
    </div>
  );
}

function FieldSpecRow({ fieldName, spec, kindsAllowed, onRename, onChange, onDelete }) {
  const allowedKinds = kindsAllowed || FIELD_KINDS;
  const kind = spec?.kind || allowedKinds[0];

  function setKind(newKind) {
    if (newKind === "inline_path") onChange({ kind: "inline_path", path: spec?.path || "" });
    else if (newKind === "dom_text") onChange({ kind: "dom_text", selector: spec?.selector || "" });
    else if (newKind === "dom_list") onChange({ kind: "dom_list", selector: spec?.selector || "", item_fields: spec?.item_fields || {} });
  }

  function setItemField(itemName, itemSpec) {
    onChange({ ...spec, item_fields: { ...(spec.item_fields || {}), [itemName]: itemSpec } });
  }
  function renameItemField(oldName, newName) {
    if (!newName || newName === oldName) return;
    const next = { ...(spec.item_fields || {}) };
    next[newName] = next[oldName];
    delete next[oldName];
    onChange({ ...spec, item_fields: next });
  }
  function deleteItemField(name) {
    const next = { ...(spec.item_fields || {}) };
    delete next[name];
    onChange({ ...spec, item_fields: next });
  }
  function addItemField() {
    const base = "field";
    let i = 1;
    const existing = new Set(Object.keys(spec.item_fields || {}));
    while (existing.has(`${base}_${i}`)) i++;
    onChange({ ...spec, item_fields: { ...(spec.item_fields || {}), [`${base}_${i}`]: { kind: "dom_text", selector: "" } } });
  }

  return (
    <div className="border border-black/10 rounded-[8px] p-2.5 bg-white space-y-2" data-testid={`field-${fieldName}`}>
      <div className="flex items-center gap-2">
        <input
          className="input flex-1 text-[12px]"
          value={fieldName}
          onChange={(e) => onRename(e.target.value)}
          placeholder="balance"
        />
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className="px-2 py-1.5 text-[11.5px] border border-black/10 rounded font-mono"
        >
          {allowedKinds.map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
        <button onClick={onDelete} className="text-neutral-500 hover:text-red-700" title="Delete field">
          <Trash2 size={13} />
        </button>
      </div>

      {kind === "inline_path" && (
        <input
          className="input font-mono text-[11.5px]"
          value={spec?.path || ""}
          onChange={(e) => onChange({ ...spec, path: e.target.value })}
          placeholder="window.WoH.AuthProfile.points"
        />
      )}
      {kind === "dom_text" && (
        <input
          className="input font-mono text-[11.5px]"
          value={spec?.selector || ""}
          onChange={(e) => onChange({ ...spec, selector: e.target.value })}
          placeholder="[data-locator=points-balance]"
        />
      )}
      {kind === "dom_list" && (
        <div className="space-y-2">
          <input
            className="input font-mono text-[11.5px]"
            value={spec?.selector || ""}
            onChange={(e) => onChange({ ...spec, selector: e.target.value })}
            placeholder=".award-row"
          />
          <div className="ml-4 pl-3 border-l-2 border-black/10 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500">Item fields</div>
              <button onClick={addItemField} className="text-[11px] text-neutral-600 hover:text-black inline-flex items-center gap-1">
                <Plus size={10} /> Add
              </button>
            </div>
            {Object.entries(spec?.item_fields || {}).length === 0 ? (
              <div className="text-[11px] text-neutral-500 italic">No item fields yet.</div>
            ) : Object.entries(spec.item_fields || {}).map(([name, sub]) => (
              <ItemFieldRow
                key={name}
                fieldName={name}
                spec={sub}
                onRename={(newName) => renameItemField(name, newName)}
                onChange={(patch) => setItemField(name, patch)}
                onDelete={() => deleteItemField(name)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FieldsEditor({ fields, kindsAllowed, onChange }) {
  const entries = Object.entries(fields || {});

  function setOne(name, spec) {
    onChange({ ...(fields || {}), [name]: spec });
  }
  function renameOne(oldName, newName) {
    if (!newName || newName === oldName) return;
    const next = { ...(fields || {}) };
    next[newName] = next[oldName];
    delete next[oldName];
    onChange(next);
  }
  function deleteOne(name) {
    const next = { ...(fields || {}) };
    delete next[name];
    onChange(next);
  }
  function addOne() {
    const base = "field";
    let i = 1;
    const existing = new Set(Object.keys(fields || {}));
    while (existing.has(`${base}_${i}`)) i++;
    const firstKind = (kindsAllowed || FIELD_KINDS)[0];
    const initial = firstKind === "inline_path" ? { kind: "inline_path", path: "" }
                   : firstKind === "dom_text"   ? { kind: "dom_text", selector: "" }
                   : { kind: "dom_list", selector: "", item_fields: {} };
    onChange({ ...(fields || {}), [`${base}_${i}`]: initial });
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500">Fields ({entries.length})</div>
        <button onClick={addOne} className="btn btn-ghost text-[11.5px] inline-flex items-center gap-1.5">
          <Plus size={11} /> Add field
        </button>
      </div>
      {entries.length === 0 ? (
        <div className="border border-dashed border-black/15 rounded-[8px] p-3 text-center text-[11.5px] text-neutral-500">
          No fields configured.
        </div>
      ) : entries.map(([name, spec]) => (
        <FieldSpecRow
          key={name}
          fieldName={name}
          spec={spec}
          kindsAllowed={kindsAllowed}
          onRename={(newName) => renameOne(name, newName)}
          onChange={(patch) => setOne(name, patch)}
          onDelete={() => deleteOne(name)}
        />
      ))}
      <div className="text-[10.5px] text-neutral-500 mt-1.5 leading-relaxed">
        Recognized field names go to db.programs: <code className="font-mono">{KNOWN_FIELDS.join(", ")}</code>. A field named <code className="font-mono">awards</code> (as a <code className="font-mono">dom_list</code>) goes to db.awards.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Extractor row — kind-aware editor. Renders the appropriate inputs and a
// preview button that POSTs /net-config/preview with the right shape.
// ---------------------------------------------------------------------------
function ExtractorRow({ programCode, extractor, onChange, onDelete }) {
  const kind = extractor.kind || "inline_js";
  const [mappingText, setMappingText] = useState(() => JSON.stringify(extractor.mappings || {}, null, 2));
  const [sampleResponseText, setSampleResponseText] = useState(
    () => extractor.sample_response ? JSON.stringify(extractor.sample_response, null, 2) : ""
  );
  const [sampleHtml, setSampleHtml] = useState(() => extractor.sample_html || "");
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [busy, setBusy] = useState(false);

  const parsedSample = useMemo(() => {
    try { return JSON.parse(sampleResponseText); } catch { return null; }
  }, [sampleResponseText]);

  function commit(patch) { onChange({ ...extractor, ...patch }); }
  function commitMappings(text) { setMappingText(text); try { commit({ mappings: JSON.parse(text) }); } catch {} }
  function commitSampleResponse(text) { setSampleResponseText(text); try { commit({ sample_response: JSON.parse(text) }); } catch {} }
  function commitSampleHtml(text) { setSampleHtml(text); commit({ sample_html: text }); }

  function setKind(newKind) {
    // When switching kinds, drop irrelevant fields so the saved doc is clean.
    const base = { id: extractor.id, label: extractor.label || "", timeout_ms: extractor.timeout_ms || 15000, required: !!extractor.required };
    if (newKind === "net_intercept") onChange({ ...base, kind: newKind, url_re: extractor.url_re || "", method: extractor.method || "GET", mappings: extractor.mappings || {}, sample_response: extractor.sample_response || null });
    else if (newKind === "inline_js") onChange({ ...base, kind: newKind, fields: extractor.fields || {} });
    else if (newKind === "dom_html")  onChange({ ...base, kind: newKind, fields: extractor.fields || {}, sample_html: extractor.sample_html || null });
    else if (newKind === "fetch_json") onChange({ ...base, kind: newKind, url_template: extractor.url_template || "", method: extractor.method || "GET", mappings: extractor.mappings || {}, sample_response: extractor.sample_response || null });
    else if (newKind === "fetch_html") onChange({ ...base, kind: newKind, url_template: extractor.url_template || "", anchor_regex: extractor.anchor_regex || null, fields: extractor.fields || {}, sample_html: extractor.sample_html || null });
  }

  async function runPreview() {
    setBusy(true); setPreviewErr(""); setPreview(null);
    try {
      const payload = { kind };
      if (kind === "net_intercept" || kind === "fetch_json") {
        try { payload.mappings = JSON.parse(mappingText); }
        catch (e) { throw new Error("mappings JSON invalid: " + e.message); }
        try { payload.sample_response = JSON.parse(sampleResponseText); }
        catch (e) { throw new Error("sample_response JSON invalid: " + e.message); }
      } else if (kind === "inline_js") {
        payload.fields = extractor.fields || {};
        try { payload.sample_response = JSON.parse(sampleResponseText); }
        catch (e) { throw new Error("sample_response JSON invalid (paste expected globals as JSON): " + e.message); }
      } else {
        payload.fields = extractor.fields || {};
        payload.sample_html = sampleHtml || "";
        if (extractor.anchor_regex) payload.anchor_regex = extractor.anchor_regex;
      }
      const { data } = await api.post(`/admin/programs/${programCode}/net-config/preview`, payload);
      setPreview(data?.result || null);
    } catch (e) {
      setPreviewErr(e?.response?.data?.detail || e.message || "preview failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="border border-black/10 rounded-[10px] p-3 bg-white space-y-3" data-testid={`extractor-${extractor.id}`}>
      <div className="flex items-center gap-2">
        <input className="input flex-1 text-[12px]" value={extractor.id || ""} onChange={(e) => commit({ id: e.target.value })} placeholder="hyatt_profile" />
        <input className="input flex-1 text-[12px]" value={extractor.label || ""} onChange={(e) => commit({ label: e.target.value })} placeholder="Member profile" />
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="px-2 py-1.5 text-[11.5px] border border-black/10 rounded font-mono">
          {EXTRACTOR_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <label className="text-[11px] text-neutral-600 inline-flex items-center gap-1 px-2">
          <input type="checkbox" checked={!!extractor.required} onChange={(e) => commit({ required: e.target.checked })} />
          required
        </label>
        <label className="text-[11px] text-neutral-600 inline-flex items-center gap-1">
          timeout&nbsp;
          <input className="input text-[11px] w-20 tabular" type="number" value={extractor.timeout_ms ?? 15000} onChange={(e) => commit({ timeout_ms: Number(e.target.value) || 15000 })} />ms
        </label>
        <button onClick={onDelete} className="text-neutral-500 hover:text-red-700" title="Delete extractor">
          <Trash2 size={13} />
        </button>
      </div>

      {(kind === "net_intercept" || kind === "fetch_json") && (
        <div className="grid grid-cols-12 gap-2">
          <label className="block col-span-9">
            <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">
              {kind === "net_intercept" ? "url_re" : "url_template"} {kind === "fetch_json" && <span className="normal-case text-neutral-400">(supports <code className="font-mono">{`{{var}}`}</code> from earlier steps)</span>}
            </div>
            <input
              className="input font-mono text-[12px]"
              value={kind === "net_intercept" ? (extractor.url_re || "") : (extractor.url_template || "")}
              onChange={(e) => commit(kind === "net_intercept" ? { url_re: e.target.value } : { url_template: e.target.value })}
              placeholder={kind === "net_intercept" ? "^https://www\\.hyatt\\.com/profile/api/member/profile" : "https://example.com/api/account.json"}
            />
          </label>
          <label className="block col-span-3">
            <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">method</div>
            <select className="input text-[12px]" value={extractor.method || "GET"} onChange={(e) => commit({ method: e.target.value })}>
              <option>GET</option><option>POST</option><option>PUT</option><option>PATCH</option><option>DELETE</option>
            </select>
          </label>
        </div>
      )}

      {kind === "fetch_html" && (
        <div className="space-y-2">
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">url_template <span className="normal-case text-neutral-400">(supports <code className="font-mono">{`{{var}}`}</code>)</span></div>
            <input className="input font-mono text-[12px]" value={extractor.url_template || ""} onChange={(e) => commit({ url_template: e.target.value })} placeholder="https://world.hyatt.com/content/gp/en/program-overview.html" />
          </label>
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">anchor_regex <span className="normal-case text-neutral-400">(locates inline JSON in the HTML)</span></div>
            <input className="input font-mono text-[12px]" value={extractor.anchor_regex || ""} onChange={(e) => commit({ anchor_regex: e.target.value })} placeholder="WoH\\.AuthProfile\\s*=" />
          </label>
        </div>
      )}

      {(kind === "net_intercept" || kind === "fetch_json") && (
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-4 border border-black/5 rounded-[8px] p-2 bg-black/[0.02] max-h-[280px] overflow-auto">
            <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">JSON sample (click any value to copy path)</div>
            {parsedSample ? <JsonTree value={parsedSample} /> : <div className="text-[11.5px] text-neutral-500">Paste valid JSON in sample_response below to inspect.</div>}
          </div>
          <div className="col-span-4">
            <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">mappings (JSON)</div>
            <textarea value={mappingText} onChange={(e) => commitMappings(e.target.value)} rows={12} className="w-full px-3 py-2 text-[11.5px] rounded-md border border-black/10 font-mono" />
            <div className="text-[10.5px] text-neutral-500 mt-1.5">
              <code className="font-mono">{`{kind:"path", path}`}</code>,
              {" "}<code className="font-mono">{`{kind:"filtered", container, where, value}`}</code>,
              {" "}<code className="font-mono">{`{kind:"collection", container, inner_container?, emit_per_item}`}</code>.
            </div>
          </div>
          <div className="col-span-4">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500">preview</div>
              <button onClick={runPreview} disabled={busy} className="btn btn-ghost text-[11.5px] inline-flex items-center gap-1">
                <FlaskConical size={11} /> Run
              </button>
            </div>
            {previewErr && (
              <div className="text-[11.5px] p-2 rounded bg-red-50 text-red-700 border border-red-200 mb-2 flex gap-1.5 items-start">
                <AlertCircle size={12} className="mt-0.5 shrink-0" /> {previewErr}
              </div>
            )}
            <pre className="text-[11.5px] font-mono bg-black/[0.02] border border-black/5 rounded-[6px] p-2 max-h-[240px] overflow-auto">
              {preview ? JSON.stringify(preview, null, 2) : <span className="text-neutral-500">Click Run after editing mappings.</span>}
            </pre>
          </div>
        </div>
      )}

      {(kind === "inline_js" || kind === "dom_html" || kind === "fetch_html") && (
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-7">
            <FieldsEditor
              fields={extractor.fields || {}}
              kindsAllowed={kind === "inline_js" ? ["inline_path"] : FIELD_KINDS}
              onChange={(next) => commit({ fields: next })}
            />
          </div>
          <div className="col-span-5">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500">preview</div>
              <button onClick={runPreview} disabled={busy} className="btn btn-ghost text-[11.5px] inline-flex items-center gap-1">
                <FlaskConical size={11} /> Run
              </button>
            </div>
            {previewErr && (
              <div className="text-[11.5px] p-2 rounded bg-red-50 text-red-700 border border-red-200 mb-2 flex gap-1.5 items-start">
                <AlertCircle size={12} className="mt-0.5 shrink-0" /> {previewErr}
              </div>
            )}
            <pre className="text-[11.5px] font-mono bg-black/[0.02] border border-black/5 rounded-[6px] p-2 max-h-[240px] overflow-auto">
              {preview ? JSON.stringify(preview, null, 2) : <span className="text-neutral-500">{kind === "inline_js" ? "Paste expected globals as JSON in sample_response below." : "Paste a sample HTML below and click Run."}</span>}
            </pre>
          </div>
        </div>
      )}

      {(kind === "net_intercept" || kind === "fetch_json" || kind === "inline_js") && (
        <div className="border-t border-black/5 pt-2">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">
            sample_response (JSON {kind === "inline_js" ? "— expected globals" : "— captured body"})
          </div>
          <textarea
            value={sampleResponseText}
            onChange={(e) => commitSampleResponse(e.target.value)}
            rows={6}
            className="w-full px-3 py-2 text-[11.5px] rounded-md border border-black/10 font-mono"
            placeholder='{"data": {...}}'
          />
        </div>
      )}

      {(kind === "dom_html" || kind === "fetch_html") && (
        <div className="border-t border-black/5 pt-2">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">sample_html (for preview)</div>
          <textarea
            value={sampleHtml}
            onChange={(e) => commitSampleHtml(e.target.value)}
            rows={6}
            className="w-full px-3 py-2 text-[11.5px] rounded-md border border-black/10 font-mono"
            placeholder="<html>...</html>"
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step card — wraps a list of extractors and the step's detect/wait/retry/advance.
// ---------------------------------------------------------------------------
function StepCard({ programCode, step, allStepIds, onChange, onDelete }) {
  function commit(patch) { onChange({ ...step, ...patch }); }
  function commitDetect(patch) { onChange({ ...step, detect: { ...(step.detect || {}), ...patch } }); }
  function commitRetry(patch)  { onChange({ ...step, retry:  { ...(step.retry  || { max_attempts: 1, interval_ms: 5000 }), ...patch } }); }
  function commitAdvance(patch){ onChange({ ...step, advance:{ ...(step.advance|| {}), ...patch } }); }

  function setExtractor(idx, ext) {
    const next = (step.extractors || []).map((x, i) => (i === idx ? ext : x));
    commit({ extractors: next });
  }
  function deleteExtractor(idx) {
    const next = (step.extractors || []).filter((_, i) => i !== idx);
    commit({ extractors: next });
  }
  function addExtractor() {
    const next = [...(step.extractors || []), {
      id: `ext_${(step.extractors || []).length + 1}`,
      kind: "net_intercept",
      label: "",
      timeout_ms: 15000,
      required: false,
      url_re: "",
      method: "GET",
      mappings: {},
      sample_response: null,
    }];
    commit({ extractors: next });
  }

  return (
    <div className="border border-black/10 rounded-[10px] p-4 mb-4 bg-white space-y-3" data-testid={`step-${step.id}`}>
      <div className="grid grid-cols-12 gap-2">
        <label className="block col-span-3">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">id</div>
          <input className="input" value={step.id || ""} onChange={(e) => commit({ id: e.target.value })} placeholder="profile" />
        </label>
        <label className="block col-span-7">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">label</div>
          <input className="input" value={step.label || ""} onChange={(e) => commit({ label: e.target.value })} placeholder="Member profile" />
        </label>
        <label className="block col-span-2">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">order</div>
          <input className="input tabular" type="number" value={step.order ?? 0} onChange={(e) => commit({ order: Number(e.target.value) || 0 })} />
        </label>

        <label className="block col-span-12">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">detect.url_re</div>
          <input className="input font-mono text-[12px]" value={step.detect?.url_re || ""} onChange={(e) => commitDetect({ url_re: e.target.value })} placeholder="^https://www\\.hyatt\\.com/profile/en-US/awards" />
        </label>
        <div className="block col-span-12">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">detect.predicate <span className="normal-case text-neutral-400">(optional JS guard, AND-ed with url_re)</span></div>
          <PredicateField
            value={step.detect?.predicate || ""}
            onChange={(v) => commitDetect({ predicate: v || null })}
            placeholder=""
            dataTestid={`step-${step.id}-detect-predicate`}
          />
        </div>

        <div className="block col-span-8">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">wait_until <span className="normal-case text-neutral-400">(JS predicate — extractors start once truthy)</span></div>
          <PredicateField
            value={step.wait_until || ""}
            onChange={(v) => commit({ wait_until: v || null })}
            placeholder="window.WoH?.AuthProfile?.goldpassportId"
            dataTestid={`step-${step.id}-wait-until`}
          />
        </div>
        <label className="block col-span-4">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">settle_ms <span className="normal-case text-neutral-400">(post-ready delay)</span></div>
          <input className="input tabular" type="number" value={step.settle_ms ?? 0} onChange={(e) => commit({ settle_ms: Math.max(0, Math.min(10000, Number(e.target.value) || 0)) })} />
        </label>

        <label className="block col-span-2">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">retry.max</div>
          <input className="input tabular" type="number" min={1} max={6} value={step.retry?.max_attempts ?? 1} onChange={(e) => commitRetry({ max_attempts: Math.max(1, Math.min(6, Number(e.target.value) || 1)) })} />
        </label>
        <label className="block col-span-3">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">retry.interval_ms (5000–30000)</div>
          <input className="input tabular" type="number" min={5000} max={30000} step={1000} value={step.retry?.interval_ms ?? 5000} onChange={(e) => commitRetry({ interval_ms: Math.max(5000, Math.min(30000, Number(e.target.value) || 5000)) })} />
        </label>
        <div className="col-span-7 text-[10.5px] text-neutral-500 self-end mb-2 leading-snug">
          Retries re-run only the failed <em>required</em> extractors. <code className="font-mono">net_intercept</code> cannot replay an XHR that already fired — retries help only if the site re-issues the request.
        </div>
      </div>

      {/* Extractors */}
      <div className="border-t border-black/5 pt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500">Extractors ({(step.extractors || []).length})</div>
          <button onClick={addExtractor} className="btn btn-ghost text-[11.5px] inline-flex items-center gap-1.5"><Plus size={11} /> Add extractor</button>
        </div>
        {(step.extractors || []).length === 0 ? (
          <div className="border border-dashed border-black/15 rounded-[8px] p-4 text-center text-[12px] text-neutral-500">No extractors yet.</div>
        ) : (step.extractors || []).map((ext, idx) => (
          <div key={idx} className="mb-3">
            <ExtractorRow programCode={programCode} extractor={ext} onChange={(e) => setExtractor(idx, e)} onDelete={() => deleteExtractor(idx)} />
          </div>
        ))}
      </div>

      {/* Advance */}
      <div className="border-t border-black/5 pt-3">
        <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-2">Advance (what happens after this step)</div>
        <div className="grid grid-cols-12 gap-2">
          <label className="block col-span-4">
            <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">next_step_id</div>
            <select className="input text-[12px]" value={step.advance?.next_step_id || ""} onChange={(e) => commitAdvance({ next_step_id: e.target.value || null })}>
              <option value="">— (reselect from current URL)</option>
              {(allStepIds || []).filter((id) => id !== step.id).map((id) => (
                <option key={id} value={id}>{id}</option>
              ))}
            </select>
          </label>
          <label className="block col-span-5">
            <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">next_url <span className="normal-case text-neutral-400">(supports <code className="font-mono">{`{{var}}`}</code>)</span></div>
            <input className="input font-mono text-[12px]" value={step.advance?.next_url || ""} onChange={(e) => commitAdvance({ next_url: e.target.value || null })} placeholder="https://www.hyatt.com/profile/{{member_id}}/awards" />
          </label>
          <label className="block col-span-3">
            <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">on_timeout</div>
            <select className="input text-[12px]" value={step.advance?.on_timeout || "advance"} onChange={(e) => commitAdvance({ on_timeout: e.target.value })}>
              <option value="advance">advance</option>
              <option value="terminate">terminate</option>
              <option value="stay">stay</option>
            </select>
          </label>
          <label className="block col-span-12 inline-flex items-center gap-2 text-[12px] text-neutral-700">
            <input type="checkbox" checked={!!step.advance?.terminate} onChange={(e) => commitAdvance({ terminate: e.target.checked })} />
            terminate after this step (close the tab — overrides next_step_id / next_url)
          </label>
        </div>
      </div>

      <div className="flex justify-end pt-2 border-t border-black/5">
        <button onClick={onDelete} className="btn btn-ghost text-[12px] text-red-700 inline-flex items-center gap-1.5">
          <Trash2 size={12} /> Delete step
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top-level program editor — steps[]
// ---------------------------------------------------------------------------
function ProgramEditor({ program, config, onSaved }) {
  const [displayName,       setDisplayName]       = useState(config?.display_name || "");
  const [hostPatternsText,  setHostPatternsText]  = useState(() => (config?.host_patterns || []).join("\n"));
  const [loginUrl,          setLoginUrl]          = useState(config?.login_url || "");
  const [loggedInCheckUrl,  setLoggedInCheckUrl]  = useState(config?.logged_in_check_url || "");
  const [loggedInWhen,      setLoggedInWhen]      = useState(config?.logged_in_when || "");
  const [steps,             setSteps]             = useState(config?.steps || []);
  const [certCodes,         setCertCodes]         = useState(() => JSON.stringify(config?.cert_codes || {}, null, 2));
  const [busy, setBusy] = useState("");
  const [err, setErr]   = useState("");

  useEffect(() => {
    setDisplayName(config?.display_name || "");
    setHostPatternsText((config?.host_patterns || []).join("\n"));
    setLoginUrl(config?.login_url || "");
    setLoggedInCheckUrl(config?.logged_in_check_url || "");
    setLoggedInWhen(config?.logged_in_when || "");
    setSteps(config?.steps || []);
    setCertCodes(JSON.stringify(config?.cert_codes || {}, null, 2));
    setErr("");
  }, [config, program.code]);

  function addStep() {
    setSteps((arr) => [...arr, {
      id: `step_${arr.length + 1}`,
      order: arr.length,
      label: "",
      detect: { url_re: "^https://", predicate: null },
      wait_until: null,
      settle_ms: 0,
      retry: { max_attempts: 1, interval_ms: 5000 },
      extractors: [],
      advance: { next_step_id: null, next_url: null, terminate: arr.length === 0, on_timeout: "advance" },
    }]);
  }

  async function save() {
    setBusy("save"); setErr("");
    try {
      let parsedCerts = {};
      try { parsedCerts = JSON.parse(certCodes || "{}"); }
      catch (e) { throw new Error("cert_codes JSON invalid: " + e.message); }
      // Parse host_patterns: one per line, comma also accepted. Trim and drop blanks.
      const hostPatterns = hostPatternsText
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const payload = {
        sync_method:         "steps",
        display_name:        displayName || null,
        host_patterns:       hostPatterns,
        login_url:           loginUrl || null,
        logged_in_check_url: loggedInCheckUrl || null,
        logged_in_when:      loggedInWhen || null,
        steps,
        cert_codes:          parsedCerts,
      };
      const { data } = await api.put(`/admin/programs/${program.code}/net-config`, payload);
      onSaved?.(data?.config);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "save failed");
    } finally {
      setBusy("");
    }
  }

  const stepIds = useMemo(() => steps.map((s) => s.id).filter(Boolean), [steps]);

  return (
    <div data-testid={`net-config-editor-${program.code}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-xl font-extrabold tracking-tight">{program.provider}</h2>
          <div className="text-[12px] text-neutral-500 font-mono">{program.code}</div>
        </div>
        <button onClick={save} disabled={!!busy} className="btn btn-primary text-[12.5px] inline-flex items-center gap-1.5">
          {busy === "save" ? <span className="spinner" /> : <Save size={13} />} Save config
        </button>
      </div>

      {err && (
        <div className="mb-3 p-2.5 rounded-[8px] text-[12.5px] flex gap-1.5 items-start" style={{ background: "var(--bad-bg)", color: "var(--bad)" }}>
          <AlertCircle size={13} className="mt-0.5 shrink-0" /> {err}
        </div>
      )}

      {/* Shell fields */}
      <div className="border border-black/10 rounded-[10px] p-4 mb-5 bg-black/[0.01] space-y-3" data-testid="program-shell">
        <div className="grid grid-cols-12 gap-3">
          <label className="block col-span-12">
            <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">display_name <span className="text-neutral-400 normal-case font-normal">(shown in the extension popup &amp; user picker)</span></div>
            <input className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Alaska Mileage Plan" />
          </label>
          <label className="block col-span-12">
            <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">
              host_patterns <span className="text-neutral-400 normal-case font-normal">(one per line — Chrome match patterns where bridge.js runs)</span>
            </div>
            <textarea
              className="w-full px-3 py-2 text-[12px] rounded-md border border-black/10 font-mono"
              rows={3}
              value={hostPatternsText}
              onChange={(e) => setHostPatternsText(e.target.value)}
              placeholder="https://*.alaskaair.com/*&#10;https://*.alaska.com/*"
            />
            <div className="text-[10.5px] text-neutral-500 mt-1 leading-relaxed">
              Programs with no host_patterns are filtered out of <code className="font-mono">/api/net/programs/supported</code> and the extension never injects on them. After saving, users may need to grant host permission via the extension popup.
            </div>
          </label>
        </div>
        <label className="block">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">login_url</div>
          <input className="input font-mono text-[12px]" value={loginUrl} onChange={(e) => setLoginUrl(e.target.value)} placeholder="https://www.hyatt.com/profile/en-US/awards" />
        </label>
        <label className="block">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">
            logged_in_check_url <span className="text-neutral-400 normal-case font-normal">(predicate only polls while on this URL)</span>
          </div>
          <input className="input font-mono text-[12px]" value={loggedInCheckUrl} onChange={(e) => setLoggedInCheckUrl(e.target.value)} placeholder="https://www.hyatt.com/profile/en-US/awards" />
        </label>
        <div className="block">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">
            logged_in_when <span className="text-neutral-400 normal-case font-normal">(JS predicate)</span>
          </div>
          <PredicateField
            value={loggedInWhen}
            onChange={setLoggedInWhen}
            placeholder="window.WoH?.AuthProfile?.goldpassportId"
            dataTestid="shell-logged-in-when"
          />
        </div>
      </div>

      {/* Steps */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display font-bold text-[14px]">steps ({steps.length})</h3>
        <button onClick={addStep} className="btn btn-ghost text-[12px] inline-flex items-center gap-1.5">
          <Plus size={12} /> Add step
        </button>
      </div>

      {steps.length === 0 ? (
        <div className="border border-dashed border-black/15 rounded-[10px] p-6 text-center text-[12.5px] text-neutral-500">
          No steps configured. Click "Add step" above.
        </div>
      ) : steps.map((s, idx) => (
        <StepCard
          key={idx}
          programCode={program.code}
          step={s}
          allStepIds={stepIds}
          onChange={(patch) => setSteps((arr) => arr.map((x, i) => (i === idx ? patch : x)))}
          onDelete={() => setSteps((arr) => arr.filter((_, i) => i !== idx))}
        />
      ))}

      <div className="mt-6 border-t border-black/10 pt-4">
        <h3 className="font-display font-bold text-[14px] mb-2">cert / tier code → display name (JSON)</h3>
        <textarea value={certCodes} onChange={(e) => setCertCodes(e.target.value)} rows={6} className="w-full px-3 py-2 text-[12px] rounded-md border border-black/10 font-mono" />
        <div className="text-[10.5px] text-neutral-500 mt-1">
          Maps short codes (e.g. <code className="font-mono">"M"</code>) to humanized display names rendered on dashboard cards.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------
export default function AdminExtensionConfig() {
  const [programs, setPrograms] = useState([]);
  const [selected, setSelected] = useState(null);
  const [config,   setConfig]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [err, setErr] = useState("");

  async function loadPrograms() {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/master-list");
      const rows = (data?.programs || [])
        .filter((p) => p.recipe_code)
        .map((p) => ({ code: p.recipe_code, provider: p.name, category: p.category }));
      setPrograms(rows);
    } catch (e) {
      setErr(e?.response?.status === 403 ? "You don't have admin access." : (e?.message || "load failed"));
    } finally { setLoading(false); }
  }

  async function loadConfig(code) {
    setLoadingConfig(true);
    try {
      const { data } = await api.get(`/admin/programs/${code}/net-config`);
      setConfig(data?.config || null);
    } catch (e) {
      setConfig(null);
    } finally { setLoadingConfig(false); }
  }

  useEffect(() => { loadPrograms(); }, []);
  useEffect(() => { if (selected) loadConfig(selected.code); }, [selected]);

  return (
    <AdminLayout
      activeTab="extension-config"
      right={
        <button onClick={loadPrograms} disabled={loading} className="btn btn-ghost text-[12.5px] inline-flex items-center gap-1.5">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      }
    >
      <div data-testid="admin-net-config-page">
        <div className="section-eyebrow">Admin · Extension sync</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Extension config</h1>
        <p className="text-[13.5px] text-neutral-600 mt-2 max-w-[720px]">
          Per-program step machine. Each step picks one or more extractors of mixed kinds
          (<code className="font-mono">net_intercept</code>, <code className="font-mono">inline_js</code>, <code className="font-mono">dom_html</code>, <code className="font-mono">fetch_json</code>, <code className="font-mono">fetch_html</code>) that run in parallel, then advances to the next step.
        </p>

        {err && (
          <div className="mt-4 p-3 border rounded-[10px] text-[13px]" style={{ background: "var(--bad-bg)", color: "var(--bad)", borderColor: "var(--bad-border)" }}>{err}</div>
        )}

        <div className="grid grid-cols-12 gap-4 mt-5">
          <div className="col-span-3 card overflow-hidden">
            <div className="px-3 py-2 text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 border-b border-black/5 bg-black/[0.02]">Programs</div>
            {loading && programs.length === 0 ? (
              <div className="px-3 py-4 text-[12.5px] text-neutral-500"><span className="spinner inline-block mr-2" /> Loading…</div>
            ) : programs.length === 0 ? (
              <div className="px-3 py-4 text-[12.5px] text-neutral-500">No programs.</div>
            ) : programs.map((p) => {
              const active = selected?.code === p.code;
              return (
                <button
                  key={p.code}
                  onClick={() => setSelected(p)}
                  className={`block w-full text-left px-3 py-2.5 border-b border-black/5 text-[12.5px] ${active ? "bg-amber-50" : "hover:bg-black/[0.02]"}`}
                  data-testid={`admin-net-config-pick-${p.code}`}
                >
                  <div className="font-semibold truncate">{p.provider}</div>
                  <div className="text-[10.5px] text-neutral-500 font-mono mt-0.5">{p.code}</div>
                </button>
              );
            })}
          </div>

          <div className="col-span-9 card p-5">
            {!selected ? (
              <div className="text-[13px] text-neutral-500 py-10 text-center">Pick a program on the left to edit its config.</div>
            ) : loadingConfig ? (
              <div className="text-[12.5px] text-neutral-500 py-6"><span className="spinner inline-block mr-2" /> Loading config…</div>
            ) : (
              <ProgramEditor
                program={selected}
                config={config}
                onSaved={(saved) => { setConfig(saved); }}
              />
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
